import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import storeService from '../../services/storeService';
import { useConstants } from '../../context/ConstantsContext';
import { useAuth } from '../../context/AuthContext'; // Import useAuth

const BasicInfoSettings = () => {
  const { constants } = useConstants();
  const { user, updateUser } = useAuth(); // Get user and updateUser from useAuth
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    businessNumber: '',
    businessType: '',
    phone: '',
    website: '',
    bankAccountName: '',
    bankBsbNumber: '',
    bankAccountNumber: '',
    bankName: '',
    timezone: 'Australia/Sydney',
    type: 'Retail Store',
    cardPaymentProvider: 'Stripe',
    lowStockThreshold: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { id: storeId } = useParams();
  const navigate = useNavigate();
  const isNew = storeId === 'new';

  const storeTypes = useMemo(() => (
    constants ? Object.values(constants.STORE_TYPES) : ['Retail Store', 'Pop Up Store', 'Warehouse']
  ), [constants]);

  const normalizeStoreType = useCallback((rawType) => {
    if (!rawType) return storeTypes[0];

    if (storeTypes.includes(rawType)) {
      return rawType;
    }

    const legacyMap = {
      Retail: constants?.STORE_TYPES?.RETAIL || 'Retail Store',
      'Pop Up': constants?.STORE_TYPES?.POPUP || 'Pop Up Store',
      Warehouse: constants?.STORE_TYPES?.WAREHOUSE || 'Warehouse',
    };

    return legacyMap[rawType] || storeTypes[0];
  }, [constants, storeTypes]);

  const normalizeOptionalNumber = useCallback((value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' && value.trim() === '') return null;
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      throw new Error('Low stock threshold must be a valid number.');
    }
    return numeric;
  }, []);

  const fetchStoreData = useCallback(async () => {
    if (isNew) {
      setIsLoading(false);
      return;
    }
    try {
      const data = await storeService.getStoreById(storeId);
      setFormData({
        name: data.name || '',
        companyName: data.companyName || '',
        businessNumber: data.businessNumber || '',
        businessType: data.businessType || '',
        phone: data.phone || '',
        website: data.website || '',
        bankAccountName: data.bankDetails?.accountName || '',
        bankBsbNumber: data.bankDetails?.bsbNumber || '',
        bankAccountNumber: data.bankDetails?.accountNumber || '',
        bankName: data.bankDetails?.bankName || '',
        timezone: data.timezone || 'Australia/Sydney',
        type: normalizeStoreType(data.type),
        cardPaymentProvider: data.cardPaymentProvider || 'Stripe',
        lowStockThreshold: data.lowStockThreshold ?? '',
      });
    } catch (err) {
      setError('Failed to fetch store data.');
    } finally {
      setIsLoading(false);
    }
  }, [storeId, isNew, normalizeStoreType]);

  useEffect(() => {
    fetchStoreData();
  }, [fetchStoreData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    try {
      let savedStore;
      const payload = {
        name: formData.name,
        companyName: formData.companyName,
        businessNumber: formData.businessNumber,
        businessType: formData.businessType,
        phone: formData.phone,
        website: formData.website,
        timezone: formData.timezone,
        type: normalizeStoreType(formData.type),
        cardPaymentProvider: formData.cardPaymentProvider,
        lowStockThreshold: normalizeOptionalNumber(formData.lowStockThreshold),
        bankDetails: {
          accountName: formData.bankAccountName,
          bsbNumber: formData.bankBsbNumber,
          accountNumber: formData.bankAccountNumber,
          bankName: formData.bankName,
        },
      };
      if (isNew) {
        savedStore = await storeService.createStore(payload);
        navigate(`/stores/settings/${savedStore._id}/basic`, { replace: true });
      } else {
        savedStore = await storeService.updateStore(storeId, payload);
        // Update the user object in the AuthContext
        const updatedStores = user.stores.map(store =>
          store._id === savedStore._id ? savedStore : store
        );
        updateUser({ ...user, stores: updatedStores });
      }
      setSuccess('Store information saved successfully!');
      setTimeout(() => setSuccess(''), 3000); // Clear success message after 3 seconds
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save store.');
    }
  };

  if (isLoading) return <div>Loading store settings...</div>;

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Basic Information</h2>
      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      {success && <p className="bg-green-100 text-green-700 p-3 rounded mb-4">{success}</p>}
      
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div><label className="block text-sm font-medium text-gray-700">Store Name*</label><input name="name" value={formData.name} onChange={handleChange} className="mt-1 p-2 border rounded-md w-full" /></div>
          <div><label className="block text-sm font-medium text-gray-700">Company Name</label><input name="companyName" value={formData.companyName} onChange={handleChange} className="mt-1 p-2 border rounded-md w-full" /></div>
          <div><label className="block text-sm font-medium text-gray-700">Business Number (ABN)</label><input name="businessNumber" value={formData.businessNumber} onChange={handleChange} className="mt-1 p-2 border rounded-md w-full" /></div>
          <div><label className="block text-sm font-medium text-gray-700">Business Type</label><input name="businessType" value={formData.businessType} onChange={handleChange} className="mt-1 p-2 border rounded-md w-full" /></div>
          <div><label className="block text-sm font-medium text-gray-700">Phone Number</label><input name="phone" value={formData.phone} onChange={handleChange} className="mt-1 p-2 border rounded-md w-full" /></div>
          <div><label className="block text-sm font-medium text-gray-700">Website</label><input name="website" value={formData.website} onChange={handleChange} className="mt-1 p-2 border rounded-md w-full" /></div>
          <div><label className="block text-sm font-medium text-gray-700">Bank Name</label><input name="bankName" value={formData.bankName} onChange={handleChange} className="mt-1 p-2 border rounded-md w-full" /></div>
          <div><label className="block text-sm font-medium text-gray-700">Account Name</label><input name="bankAccountName" value={formData.bankAccountName} onChange={handleChange} className="mt-1 p-2 border rounded-md w-full" /></div>
          <div><label className="block text-sm font-medium text-gray-700">BSB Number</label><input name="bankBsbNumber" value={formData.bankBsbNumber} onChange={handleChange} className="mt-1 p-2 border rounded-md w-full" /></div>
          <div><label className="block text-sm font-medium text-gray-700">Account Number</label><input name="bankAccountNumber" value={formData.bankAccountNumber} onChange={handleChange} className="mt-1 p-2 border rounded-md w-full" /></div>
          <div><label className="block text-sm font-medium text-gray-700">Timezone</label><select name="timezone" value={formData.timezone} onChange={handleChange} className="mt-1 p-2 border rounded-md w-full"><option value="Australia/Sydney">Sydney</option><option value="Australia/Melbourne">Melbourne</option><option value="Australia/Brisbane">Brisbane</option><option value="Australia/Perth">Perth</option><option value="Australia/Adelaide">Adelaide</option></select></div>
          <div><label className="block text-sm font-medium text-gray-700">Store Type*</label><select name="type" value={formData.type} onChange={handleChange} className="mt-1 p-2 border rounded-md w-full">{storeTypes.map(type => (<option key={type} value={type}>{type}</option>))}</select></div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Default Low Stock Threshold</label>
            <input
              type="number"
              name="lowStockThreshold"
              value={formData.lowStockThreshold}
              onChange={handleChange}
              className="mt-1 p-2 border rounded-md w-full"
              min="0"
              step="1"
              placeholder="Leave blank to use system default"
            />
          </div>
          {/* Add this dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Card Payment Provider</label>
            <select name="cardPaymentProvider" value={formData.cardPaymentProvider} onChange={handleChange} className="mt-1 p-2 border rounded-md w-full">
              <option value="Stripe">Stripe</option>
              <option value="Tyro">Tyro</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end pt-4">
          <button onClick={handleSave} className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark">
            {isNew ? 'Create Store' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoSettings;
